import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../App";
import { useLanguage } from "../App";
import { useTheme } from "../App";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { NotificationToggle } from "../components/PWAInstallBanner";
import {
  Calendar,
  Clock,
  MapPin,
  Phone,
  User,
  LogOut,
  Sun,
  Moon,
  Globe,
  ChevronRight,
  CheckCircle2,
  XCircle,
  History,
  CalendarCheck,
  Store,
  Plus,
  AlertCircle,
  Bell
} from "lucide-react";

export default function ClientPortal() {
  const { user, logout } = useAuth();
  const { t, language, changeLanguage } = useLanguage();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [appointments, setAppointments] = useState({ upcoming: [], past: [] });
  const [cancellingId, setCancellingId] = useState(null);
  const [activeTab, setActiveTab] = useState("upcoming");

  const translations = {
    it: {
      myPortal: "Il Mio Portale",
      welcome: "Bentornato",
      upcomingAppointments: "Prossimi Appuntamenti",
      pastAppointments: "Storico Appuntamenti",
      noUpcoming: "Nessun appuntamento in programma",
      noPast: "Nessun appuntamento passato",
      bookNow: "Prenota Ora",
      cancel: "Cancella",
      cancelling: "Cancellando...",
      confirmCancel: "Conferma Cancellazione",
      cancelSuccess: "Appuntamento cancellato",
      totalAppointments: "Totale Appuntamenti",
      completed: "Completati",
      cancelled: "Cancellati",
      favoriteSalons: "I Tuoi Saloni Preferiti",
      visits: "visite",
      at: "alle",
      with: "con",
      viewSalon: "Vai al Salone",
      pendingConfirmation: "Attende Conferma",
      confirmed: "Confermato",
      pending: "In Attesa",
      duration: "Durata",
      minutes: "min"
    },
    en: {
      myPortal: "My Portal",
      welcome: "Welcome back",
      upcomingAppointments: "Upcoming Appointments",
      pastAppointments: "Past Appointments",
      noUpcoming: "No upcoming appointments",
      noPast: "No past appointments",
      bookNow: "Book Now",
      cancel: "Cancel",
      cancelling: "Cancelling...",
      confirmCancel: "Confirm Cancellation",
      cancelSuccess: "Appointment cancelled",
      totalAppointments: "Total Appointments",
      completed: "Completed",
      cancelled: "Cancelled",
      favoriteSalons: "Your Favorite Salons",
      visits: "visits",
      at: "at",
      with: "with",
      viewSalon: "Go to Salon",
      pendingConfirmation: "Awaiting Confirmation",
      confirmed: "Confirmed",
      pending: "Pending",
      duration: "Duration",
      minutes: "min"
    }
  };

  const txt = translations[language] || translations.it;

  const fetchData = useCallback(async () => {
    try {
      const [profileRes, appointmentsRes] = await Promise.all([
        axios.get(`${API}/client/profile`, { withCredentials: true }),
        axios.get(`${API}/client/appointments`, { withCredentials: true })
      ]);
      setProfile(profileRes.data);
      setAppointments(appointmentsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      if (error.response?.status === 401) {
        navigate("/login");
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCancel = async (appointmentId) => {
    if (!window.confirm("Sei sicuro di voler cancellare questo appuntamento?")) {
      return;
    }
    
    setCancellingId(appointmentId);
    try {
      await axios.post(`${API}/client/appointments/${appointmentId}/cancel`, {}, { withCredentials: true });
      toast.success(txt.cancelSuccess);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nella cancellazione");
    } finally {
      setCancellingId(null);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(language === "it" ? "it-IT" : "en-US", {
      weekday: "long",
      day: "numeric",
      month: "long"
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: txt.pending, variant: "secondary" },
      awaiting_confirmation: { label: txt.pendingConfirmation, variant: "warning" },
      confirmed: { label: txt.confirmed, variant: "success" },
      completed: { label: txt.completed, variant: "default" },
      cancelled: { label: txt.cancelled, variant: "destructive" },
      no_show: { label: "No Show", variant: "destructive" },
      unconfirmed: { label: "Non Confermato", variant: "warning" }
    };
    const config = statusConfig[status] || statusConfig.pending;
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse-glow">
          <div className="w-12 h-12 rounded-full bg-accent/20 flex items-center justify-center">
            <div className="w-6 h-6 rounded-full bg-accent animate-pulse"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_saloneos-booking/artifacts/eysotq4t_smartbooker%20logo.png" 
              alt="SmartBooker" 
              className="h-8 w-auto"
            />
          </Link>
          
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => changeLanguage(language === "it" ? "en" : "it")}
              data-testid="language-toggle"
            >
              <Globe className="w-5 h-5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              data-testid="theme-toggle"
            >
              {theme === "light" ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
            </Button>
            <Button variant="ghost" size="icon" onClick={logout} data-testid="logout-button">
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2" data-testid="welcome-heading">
            {txt.welcome}, {user?.name?.split(" ")[0] || "Cliente"}! 👋
          </h1>
          <p className="text-muted-foreground">{txt.myPortal}</p>
        </div>

        {/* Stats Cards */}
        {profile?.stats && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <Card data-testid="stats-total">
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-full bg-blue-500/10 text-blue-500">
                    <CalendarCheck className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">{txt.totalAppointments}</p>
                    <p className="text-2xl font-bold">{profile.stats.total_appointments}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card data-testid="stats-completed">
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-full bg-green-500/10 text-green-500">
                    <CheckCircle2 className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">{txt.completed}</p>
                    <p className="text-2xl font-bold">{profile.stats.completed}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card data-testid="stats-cancelled">
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-full bg-red-500/10 text-red-500">
                    <XCircle className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">{txt.cancelled}</p>
                    <p className="text-2xl font-bold">{profile.stats.cancelled}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Push Notifications Settings */}
        <Card className="mb-8" data-testid="notification-settings-card">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Bell className="w-5 h-5" />
              {language === "it" ? "Notifiche" : "Notifications"}
            </CardTitle>
            <CardDescription>
              {language === "it" 
                ? "Ricevi promemoria per i tuoi appuntamenti direttamente sul tuo dispositivo"
                : "Receive reminders for your appointments directly on your device"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <NotificationToggle language={language} />
          </CardContent>
        </Card>

        {/* Favorite Salons */}
        {profile?.stats?.favorite_salons?.length > 0 && (
          <Card className="mb-8" data-testid="favorite-salons">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Store className="w-5 h-5" />
                {txt.favoriteSalons}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {profile.stats.favorite_salons.map((salon, idx) => (
                  <Link
                    key={idx}
                    to={`/book/${salon.slug}`}
                    className="p-4 rounded-lg border border-border hover:border-accent transition-colors flex items-center justify-between"
                    data-testid={`favorite-salon-${idx}`}
                  >
                    <div>
                      <p className="font-medium">{salon.name}</p>
                      <p className="text-sm text-muted-foreground">{salon.visits} {txt.visits}</p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-muted-foreground" />
                  </Link>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Appointments Tabs */}
        <div className="flex gap-2 mb-6">
          <Button
            variant={activeTab === "upcoming" ? "default" : "outline"}
            onClick={() => setActiveTab("upcoming")}
            data-testid="tab-upcoming"
          >
            <CalendarCheck className="w-4 h-4 mr-2" />
            {txt.upcomingAppointments} ({appointments.upcoming.length})
          </Button>
          <Button
            variant={activeTab === "past" ? "default" : "outline"}
            onClick={() => setActiveTab("past")}
            data-testid="tab-past"
          >
            <History className="w-4 h-4 mr-2" />
            {txt.pastAppointments} ({appointments.past.length})
          </Button>
        </div>

        {/* Appointments List */}
        <div className="space-y-4">
          {activeTab === "upcoming" && (
            <>
              {appointments.upcoming.length === 0 ? (
                <Card data-testid="no-upcoming">
                  <CardContent className="py-12 text-center">
                    <Calendar className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                    <p className="text-muted-foreground mb-4">{txt.noUpcoming}</p>
                    <Button asChild>
                      <Link to="/book/demo-salon" data-testid="book-now-button">
                        <Plus className="w-4 h-4 mr-2" />
                        {txt.bookNow}
                      </Link>
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                appointments.upcoming.map((appt) => (
                  <Card key={appt.appointment_id} data-testid={`appointment-${appt.appointment_id}`}>
                    <CardContent className="p-6">
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-lg">{appt.service_name}</h3>
                            {getStatusBadge(appt.status)}
                          </div>
                          
                          <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Store className="w-4 h-4" />
                              {appt.salon_name}
                            </span>
                            <span className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              {formatDate(appt.date)}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              {appt.time} ({appt.service_duration} {txt.minutes})
                            </span>
                            <span className="flex items-center gap-1">
                              <User className="w-4 h-4" />
                              {txt.with} {appt.staff_name}
                            </span>
                          </div>
                          
                          {appt.salon_address && (
                            <p className="text-sm text-muted-foreground flex items-center gap-1">
                              <MapPin className="w-4 h-4" />
                              {appt.salon_address}
                            </p>
                          )}
                          
                          {appt.status === "awaiting_confirmation" && appt.confirmation_token && (
                            <div className="flex items-center gap-2 text-amber-600 text-sm">
                              <AlertCircle className="w-4 h-4" />
                              <Link 
                                to={`/confirm/${appt.confirmation_token}`}
                                className="underline hover:no-underline"
                              >
                                Conferma il tuo appuntamento
                              </Link>
                            </div>
                          )}
                        </div>
                        
                        <div className="flex gap-2">
                          {appt.salon_slug && (
                            <Button variant="outline" size="sm" asChild>
                              <Link to={`/book/${appt.salon_slug}`}>
                                {txt.viewSalon}
                              </Link>
                            </Button>
                          )}
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => handleCancel(appt.appointment_id)}
                            disabled={cancellingId === appt.appointment_id}
                            data-testid={`cancel-${appt.appointment_id}`}
                          >
                            {cancellingId === appt.appointment_id ? txt.cancelling : txt.cancel}
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </>
          )}

          {activeTab === "past" && (
            <>
              {appointments.past.length === 0 ? (
                <Card data-testid="no-past">
                  <CardContent className="py-12 text-center">
                    <History className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                    <p className="text-muted-foreground">{txt.noPast}</p>
                  </CardContent>
                </Card>
              ) : (
                appointments.past.map((appt) => (
                  <Card key={appt.appointment_id} className="opacity-75" data-testid={`past-${appt.appointment_id}`}>
                    <CardContent className="p-6">
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold">{appt.service_name}</h3>
                            {getStatusBadge(appt.status)}
                          </div>
                          
                          <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Store className="w-4 h-4" />
                              {appt.salon_name}
                            </span>
                            <span className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              {formatDate(appt.date)}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              {appt.time}
                            </span>
                            <span className="flex items-center gap-1">
                              <User className="w-4 h-4" />
                              {appt.staff_name}
                            </span>
                          </div>
                          
                          <p className="text-sm font-medium">€{appt.service_price?.toFixed(2)}</p>
                        </div>
                        
                        {appt.salon_slug && (
                          <Button variant="outline" size="sm" asChild>
                            <Link to={`/book/${appt.salon_slug}`}>
                              <Plus className="w-4 h-4 mr-1" />
                              {txt.bookNow}
                            </Link>
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
}
